/* eslint-disable arrow-body-style */
import { useMutation } from '@apollo/client';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faEdit, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { ChangeEvent, FC, useState } from 'react';
import Button from '../../../../../components/form/button';
import Unbutton from '../../../../../components/form/button/unbutton';
import Switch from '../../../../../components/form/switch';
import Loader from '../../../../../components/loader';
import ModalConfirm from '../../../../../components/modal/confirm';
import Modal from '../../../../../components/modal/default';
import {
  USERS_CLINIC_ADMIN_DELETE,
  USERS_CLINIC_ADMIN_UPDATE,
} from '../../../../../graphql/mutation/users';
import useAuth from '../../../../../hooks/auth';
import useToaster from '../../../../../hooks/toast';
import { Clinic, GraphQLResponse, IUser } from '../../../../../types';
import { logger } from '../../../../../utils/logger';
import styles from './styles.module.css';

interface IUserRow {
  user: IUser;
  refresh: () => void;
  availableClinics: Clinic[];
}

const User: FC<IUserRow> = props => {
  const { user, refresh, availableClinics } = props;

  const { id, email, nameFirst, nameLast, role, acl } = user;

  // graphql brings in acl array as all strings, but auth user correctly
  // has numbers as they should be. this has to be handled
  const clinicsClean = !acl.clinics ? [] : acl.clinics.map(c => Number(c));

  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [isSuper, setIsSuper] = useState(role.name === 'clinicsuperadmin');
  const [clinicsNext, setClinicsNext] = useState(clinicsClean);
  const { user: currentUser } = useAuth();
  const { success, danger } = useToaster();

  const [del, { loading: loadingDel }] = useMutation<
    GraphQLResponse<'deleteUserClinicAdmin', string>
  >(USERS_CLINIC_ADMIN_DELETE, { errorPolicy: 'all' });

  const [update, { loading: loadingUpd }] = useMutation<
    GraphQLResponse<'updateUserClinicAdmin', IUser>
  >(USERS_CLINIC_ADMIN_UPDATE, { errorPolicy: 'all' });


  const handleShowConfirm = () => {
    setIsOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setIsOpenConfirm(false);
  };

  const handleConfirmDelete = () => {
    setIsOpenConfirm(false);
    del({ variables: { userId: id } })
      .then(res => {
        if (res.errors && res.errors.length > 0) {
          const errorMessage = res.errors.map(e => e.message).join('. ');
          danger(`Error: ${errorMessage}`);
          return;
        }
        success('User deleted successfully.');
        refresh();
      })
      .catch(err => {
        danger('Sorry, an unknown error occurred');
        logger.error(new Error(`Error deleting user: ${err.message}`));
      });
  };

  const handleShowEdit = () => {
    setIsOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setIsOpenEdit(false);
  };

  const handleSetSuper = (e: ChangeEvent<HTMLInputElement>) => {
    setIsSuper(e.target.checked);
  };

  const hadClinicId = (clinicId: string): boolean => {
    return clinicsClean.includes(Number(clinicId));
  };

  const handleSetClinics = (clinicId: string) => (e: ChangeEvent<HTMLInputElement>) => {
    // add clinicId
    if (e.target.checked) return setClinicsNext([...clinicsNext, Number(clinicId)]);

    // remove clinicId
    setClinicsNext(clinicsNext.filter(c => c !== Number(clinicId)));
  };

  const handleUpdate = () => {
    handleCloseEdit();

    // check super/regular value to see if changed
    let promote;
    let demote;
    if (role.name !== 'clinicsuperadmin' && isSuper) {
      // they got promoted!
      promote = true;
    }
    if (role.name === 'clinicsuperadmin' && !isSuper) {
      // they got demoted!
      demote = true;
    }

    update({
      variables: {
        input: {
          userId: id,
          ...(promote && { promote }),
          ...(demote && { demote }),
          clinicsNext: clinicsNext,
        },
      },
    })
      .then(res => {
        if (res.errors && res.errors.length > 0) {
          const errorMessage = res.errors.map(e => e.message).join('. ');
          danger(`Error: ${errorMessage}`);
          return;
        }
        success('User updated successfully.');
        refresh();
      })
      .catch(err => {
        danger('Sorry, an unknown error occurred');
        logger.error(new Error(`Error updating user: ${err.message}`));
      });
  };

  return (
    <div className={styles.row}>
      <div className={styles.rowLevel}>
        <div className={styles.rowLeft}>
          <div className={styles.rowItem}>
            <span className={styles.label}>
              {nameFirst} {nameLast}
            </span>
          </div>
          <div className={clsx(styles.rowItem, styles.textMuted)}>{email}</div>
        </div>
        <div className={styles.rowRight}>
          <div className={clsx(styles.rowItem, styles.textMuted)}>{role.nameDisplay}</div>

          {/* don't show buttons for current user */}
          {user.id !== currentUser?.userId && (
            <div className={styles.rowMenu}>
              <Unbutton onClick={handleShowEdit}>
                <FontAwesomeIcon icon={faEdit as IconProp} size="lg" className={styles.edit} />
              </Unbutton>

              <Unbutton onClick={handleShowConfirm}>
                <FontAwesomeIcon
                  icon={faTimesCircle as IconProp}
                  size="lg"
                  className={styles.delete}
                />
              </Unbutton>
            </div>
          )}
        </div>
      </div>

      <Loader loading={loadingDel || loadingUpd} />

      <ModalConfirm
        title="This action can't be undone"
        isOpen={isOpenConfirm}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmDelete}
        confirmText="Delete">
        <p className={styles.modalBodyText}>Are you sure you want to delete this user?</p>
      </ModalConfirm>

      <Modal
        isOpen={isOpenEdit}
        onClose={handleCloseEdit}
        title="Edit User Permissions"
        contentProps={{
          style: {
            padding: '1.5rem 0 1.5rem 2.5rem',
            flexDirection: 'column',
          },
        }}
        style={{
          content: {
            width: '80%',
            maxWidth: '950px',
          },
        }}>
        <div>
          <h2 className="is-size-5">Super Clinic Admin</h2>

          <div className={styles.paddingBottom}>
            <Switch defaultChecked={isSuper} onChange={handleSetSuper} />
          </div>

          <h2 className="is-size-5">Clinic Access</h2>

          <div className={clsx(styles.table, styles.paddingBottom)}>
            {/* handle edge case of current user not having clinics */}
            {!availableClinics || !availableClinics.length ? (
              "Whoa! Your user doesn't have access to any clinics!"
            ) : (
              availableClinics.map((clinic, i) => (
                <Switch
                  key={i}
                  label={clinic.displayName}
                  defaultChecked={hadClinicId(clinic.id.toString())}
                  onChange={handleSetClinics(clinic.id.toString())}
                />
              ))
            )}
          </div>
        </div>

        <div className="buttons">
          <Button onClick={handleCloseEdit} disabled={loadingUpd}>
            Cancel
          </Button>

          <Button
            onClick={handleUpdate}
            variant="dark"
            isLoading={loadingUpd}
            disabled={loadingUpd}>
            Update
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default User;
